/* eslint-disable @typescript-eslint/no-explicit-any */
import { useMasterParams, useOdnTypes } from "@/api/master-param";
import { useBpmnMediaTempCreate } from "@/api/media";
import {
  RFSData,
  useGenerateRFSInvalidXlsx,
  useRFSCreateStart,
  useRFSInvalids,
} from "@/api/rfs";
import { useAPDs } from "@/api/trackerdata-apd";
import { FieldCombobox } from "@/components/form/field-combobox";
import { FieldFile } from "@/components/form/field-file";
import { FieldInput } from "@/components/form/field-input";
import { FieldInputDate } from "@/components/form/field-input-date";
import { FieldSelect } from "@/components/form/field-select";
import { FieldTextarea } from "@/components/form/field-textarea";
import {
  FieldsetThreeColumn,
  FormContainer,
  Legend,
} from "@/components/form/form-container";
import { FormFooter } from "@/components/form/form-footer";
import { Anchor } from "@/components/ui/anchor";
import {
  DefaultButton,
  DownloadButton,
  SubmitButton,
} from "@/components/ui/button";
import { VerticalGaps } from "@/components/ui/container";
import { InputWithLabel } from "@/components/ui/input-with-label";
import { REQUIRED_MESSAGE, ROLE } from "@/utils/constant";
import { swalError, swalSuccess } from "@/utils/lib/swal";
import { zodResolver } from "@hookform/resolvers/zod";
import { AxiosError } from "axios";
import { useEffect, useId, useState } from "react";
import { useForm } from "react-hook-form";
import { useNavigate } from "react-router-dom";
import { z } from "zod";
import { Dialog, DialogActions, DialogContent } from "@mui/material";
import { CheckSquare2, SquareX } from "lucide-react";
import { RFSInvalidTable } from "./RFSInvalidTable";
import { useCurrentUser } from "../auth/useCurrentUser";
import { useProfileByKeycloakId } from "@/api/auth";
import { FieldHomepassAdditional, FieldHomepassInvalid } from "./document/ui";
import { useProjectByCLUSTERID } from "@/api/project-monitoring";

const rfsCreateFormSchema = z.object({
  clusterId: z.string().min(1, { message: REQUIRED_MESSAGE }),
  projectId: z.string().min(1, { message: REQUIRED_MESSAGE }),
  odnType: z.string().min(1, { message: REQUIRED_MESSAGE }),
  media: z
    .instanceof(FileList)
    .refine((value) => value.length > 0, {
      message: REQUIRED_MESSAGE,
    })
    .refine(
      (fileList) => fileList === null || fileList[0]?.name?.endsWith(".kmz"),
      {
        message: "Please upload files in .kmz format only",
      },
    ),
  totalBizzHomepass: z
    .number()
    .or(
      z.string().min(1, {
        message: REQUIRED_MESSAGE,
      }),
    )
    .pipe(z.coerce.number()),
  totalResidenceHomepass: z
    .number()
    .or(
      z.string().min(1, {
        message: REQUIRED_MESSAGE,
      }),
    )
    .pipe(z.coerce.number()),
  dateFeederDone: z.string().optional(),
  dateOltDone: z.string().optional(),
  remarks: z.string().optional(),
  fdtType: z.string().min(1, { message: REQUIRED_MESSAGE }),
  category: z.string().min(1, { message: REQUIRED_MESSAGE }),

  // conditional fields (optional in schema; validated in handleSubmit)
  homepassInvalid: z
    .number()
    .or(z.string().optional())
    .optional()
    .transform((v) => (v === undefined || v === "" ? undefined : Number(v))),
  homepassAdditional: z
    .number()
    .or(z.string().optional())
    .optional()
    .transform((v) => (v === undefined || v === "" ? undefined : Number(v))),
});
type RFSCreateFormSchema = z.infer<typeof rfsCreateFormSchema>;

export function RFSCreateForm() {
  const [clusterIdSearch, setClusterIdSearch] = useState("");
  const [projectIdSearch, setProjectIdSearch] = useState("");
  const [downloadResultButton, setDownloadResultButton] = useState(false);
  const [rfsId, setRfsId] = useState<string | undefined>(undefined);
  const [openDialog, setOpenDialog] = useState(false);

  const navigate = useNavigate();
  const user = useCurrentUser();
  const { data: profile } = useProfileByKeycloakId({ keycloakId: user.id });
  const userCompanyCode = profile?.companyCode;
  const isVendor = user.roles.includes(ROLE.VENDOR);

  const { mutateAsync: mutateMedia, isPending: isUploadingMedia } =
    useBpmnMediaTempCreate();
  const { mutate: mutateRfs, isPending } = useRFSCreateStart();

  const { data: odnTypes } = useOdnTypes();
  const odnTypesOptions = odnTypes?.content?.map((odnType) => ({
    value: odnType.value,
    label: odnType.value,
  }));

  const { mutate: generateRFSInvalid, isPending: isDownloading } =
    useGenerateRFSInvalidXlsx();

  const form = useForm<RFSCreateFormSchema>({
    resolver: zodResolver(rfsCreateFormSchema),
    mode: "onChange",
  });
  const formId = useId();

  // read reactive validity
  const {
    formState: { isValid },
  } = form;

  /* ===== Options ===== */
  const { data: fdtTypes } = useMasterParams({
    paramType: "ITV_FDT_TYPE",
    active: "true",
    asc: "true",
  });

  const fdtTypeOptions = fdtTypes?.content.map((status) => ({
    value: status.value,
    label: status.value,
  }));

  const { data: dataCategories } = useMasterParams({
    paramType: "ITV_CATEGORY_DATA",
    active: "true",
    asc: "true",
  });

  const dataCategoryOptions = dataCategories?.content.map((status) => ({
    value: status.value,
    label: status.value,
  }));

  /* =============== */

  /* ===== APD ===== */
  const { data: apds } = useAPDs({
    vendor: isVendor ? userCompanyCode : undefined,
    search: clusterIdSearch,
  });
  const clusterIdOptions = optionsFromProperty<{
    clusterId: string;
  }>(apds?.content || [], "clusterId");
  const currentClusterId = form.watch("clusterId");
  const apdsFilteredByClusterId = apds?.content?.filter(
    (apd) => apd.clusterId === currentClusterId,
  );
  const apd =
    (apdsFilteredByClusterId ?? []).length === 1
      ? apdsFilteredByClusterId?.[0]
      : undefined;
  const apdVendorCode = apd?.vendor;
  /* =============== */

  /* ===== Project ===== */
  const { data: projects } = useProjectByCLUSTERID({
    ClusterID: currentClusterId,
  });
  const { data: dataProjects } = useMasterParams(
    {
      paramType: "VENDOR_INHOUSE",
      active: "true",
      asc: "true",
      paramTag: apdVendorCode,
    },
    {
      enabled: !!apdVendorCode,
    },
  );

  const projectIdsOptions =
    projects?.map((project) => ({
      value: project.ProjectID,
      label: project.ProjectID,
    })) ?? [];
  const vendorInhouseProjectIds =
    dataProjects?.content.map((item) => ({
      value: item.value,
      label: item.value,
    })) ?? [];

  const isVendorProjectIdOptionsAvailable = vendorInhouseProjectIds.length > 0;

  const currentProjectId = form.watch("projectId");
  const currentProject = isVendorProjectIdOptionsAvailable
    ? dataProjects?.content.find(
        (project) => project.value === currentProjectId,
      )
    : projects?.find((project) => project.ProjectID === currentProjectId);
  const currentProjectDescription = isVendorProjectIdOptionsAvailable
    ? (currentProject as any)?.value
    : (currentProject as any)?.ProjectDescription;
  /* =================== */

  /* ===== Total HP RFS ===== */
  const totalResidenceHomepass = parseInt(
    (form.watch("totalResidenceHomepass") as unknown as string) || "0",
    10,
  );
  const totalBizzHomepass = parseInt(
    (form.watch("totalBizzHomepass") as unknown as string) || "0",
    10,
  );
  const totalHpRfs = (totalResidenceHomepass + totalBizzHomepass).toString();

  const hpApdInt = parseInt((apd?.hpDesign as unknown as string) || "0", 10); // NOSONAR
  const totalHpRfsInt = totalResidenceHomepass + totalBizzHomepass;
  /* ======================== */

  function handleDownload() {
    generateRFSInvalid(
      {
        id: rfsId,
        name: `RFS Validation Result-${currentClusterId}.xlsx`,
      },
      {
        onError: () => {
          swalError.fire();
        },
      },
    );
  }

  const watchedResidence = Number(form.watch("totalResidenceHomepass") ?? 0);
  const watchedBizz = Number(form.watch("totalBizzHomepass") ?? 0);

  const totalRfs = watchedResidence + watchedBizz;
  const hpApd = parseInt((apd?.hpDesign as unknown as string) || "0", 10); // NOSONAR

  const needInvalid = totalRfs < hpApd;
  const needAdditional = totalRfs > hpApd;
  const requiredInvalidCount = needInvalid ? hpApd - totalRfs : 0;
  const requiredAdditionalCount = needAdditional ? totalRfs - hpApd : 0;

  /*
    Side-effect: update/clear form errors when watched values change.
    Avoid doing this directly during render to prevent render loops.
  */
  const watchedHomepassInvalid = form.watch("homepassInvalid");
  const watchedHomepassAdditional = form.watch("homepassAdditional");

  /* v8 ignore start */
  useEffect(() => {
    // If APD not loaded yet, skip
    if (apd === undefined) return;

    if (needInvalid) {
      // live value from watch
      const v = watchedHomepassInvalid;
      if (v !== undefined && Number(v) === requiredInvalidCount) {
        // correct value -> clear error
        form.clearErrors("homepassInvalid");
      } else {
        // missing or incorrect -> set error
        form.setError("homepassInvalid", {
          type: "manual",
          message: `Total Homepass Invalid must be ${requiredInvalidCount}`,
        });
      }
      // always clear the other conditional error when this branch is active
      form.clearErrors("homepassAdditional");
    } else if (needAdditional) {
      const v = watchedHomepassAdditional;
      if (v !== undefined && Number(v) === requiredAdditionalCount) {
        form.clearErrors("homepassAdditional");
      } else {
        form.setError("homepassAdditional", {
          type: "manual",
          message: `Total Homepass Additional must be ${requiredAdditionalCount}`,
        });
      }
      form.clearErrors("homepassInvalid");
    } else {
      // no diff => clear both
      form.clearErrors(["homepassInvalid", "homepassAdditional"]);
    }
    // dependencies: include watched values so effect runs on user input
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    watchedResidence,
    watchedBizz,
    watchedHomepassInvalid,
    watchedHomepassAdditional,
    apd?.hpDesign,
    needInvalid,
    needAdditional,
    requiredInvalidCount,
    requiredAdditionalCount,
  ]);
  /* v8 ignore stop */

  async function handleSubmit(values: RFSCreateFormSchema) {
    if (!apd) {
      throw new Error("APD not found");
    }

    /* v8 ignore start */
    // conditional validations
    if (needInvalid) {
      const provided = values.homepassInvalid;
      if (provided === undefined || Number(provided) !== requiredInvalidCount) {
        form.setError("homepassInvalid", {
          type: "manual",
          message: `Total Homepass Invalid must be ${requiredInvalidCount}`,
        });
        return;
      }
    }

    if (needAdditional) {
      const provided = values.homepassAdditional;
      if (
        provided === undefined ||
        Number(provided) !== requiredAdditionalCount
      ) {
        form.setError("homepassAdditional", {
          type: "manual",
          message: `Total Homepass Additional must be ${requiredAdditionalCount}`,
        });
        return;
      }
    }
    /* v8 ignore stop */

    mutateMedia(
      {
        data: {
          file: values.media[0],
          target: "/abd-document",
        },
      },
      {
        onSuccess: (result) => {
          mutateRfs(
            {
              data: {
                odnType: values.odnType,
                projectId: values.projectId,
                projectDescription: currentProjectDescription,
                trackerdataApd: { id: apd.id },
                media: {
                  id: result.id,
                  path: result.path,
                  originalFileName: result.originalFileName,
                  name: result.name,
                  type: result.type,
                },
                totalResidenceHomepass: values.totalResidenceHomepass,
                totalBizzHomepass: values.totalBizzHomepass,
                dateFeederDone: values.dateFeederDone,
                dateOltDone: values.dateOltDone,
                remarks: values.remarks,
                fdtType: values.fdtType,
                category: values.category,
              },
              params: {
                withVariable: "true",
              },
            },
            {
              onSuccess: handleSuccess,
              onError: (error) => {
                const err = error as AxiosError;
                const errorMessage = (err.response?.data as { data: unknown })
                  ?.data;

                form.setValue("media", new DataTransfer().files);

                swalError.fire({
                  text: `${errorMessage}`,
                });
              },
            },
          );
        },
        onError: (error) => {
          const err = error as AxiosError;
          const errorMessage = (err.response?.data as { data: unknown })?.data;

          form.setValue("media", new DataTransfer().files);

          swalError.fire({
            title: "Error",
            text: (errorMessage as string) ?? "Failed to submit RFS",
          });
        },
      },
    );
  }

  function handleSuccess(result: RFSData) {
    setRfsId(result.object.id);

    if (result.serviceLogs?.length > 0) {
      const remarks = result.serviceLogs.map((log) => log.remarks);
      swalError
        .fire({
          title: "RFS REVISION",
          text: remarks.join("\n"),
        })
        .then(() => {
          // Show download result button
          setOpenDialog(true);
          setDownloadResultButton(true);
          navigate(`/rfs/rfs/${result.object.id}/draft`);
        });
    } else {
      swalSuccess
        .fire({
          title: "Submission Successful",
          text: "You have successfully submitted on RFS application",
        })
        .then(() => {
          navigate("/rfs/rfs");
        });
    }
  }

  return (
    <>
      {rfsId && (
        <Dialog
          open={openDialog}
          onClose={() => setOpenDialog(false)}
          aria-labelledby="alert-dialog-title"
          aria-describedby="alert-dialog-description"
        >
          <DialogContent>
            <ModalRFSInvalid rfsId={rfsId} />
          </DialogContent>
          <DialogActions>
            <DefaultButton
              mood="axiata-blue"
              onClick={() => setOpenDialog(false)}
            >
              OK
            </DefaultButton>
          </DialogActions>
        </Dialog>
      )}

      <FormContainer id={formId} form={form} onSubmit={handleSubmit}>
        <FieldsetThreeColumn>
          <Legend>Input by master APD</Legend>

          <VerticalGaps>
            <FieldCombobox
              name="clusterId"
              label="Cluster ID"
              search={clusterIdSearch}
              setSearch={setClusterIdSearch}
              options={clusterIdOptions}
              hasRequiredAsterisk
            />
          </VerticalGaps>

          <FieldCombobox
            name="projectId"
            label="Project ID"
            search={projectIdSearch}
            setSearch={setProjectIdSearch}
            options={
              isVendorProjectIdOptionsAvailable
                ? vendorInhouseProjectIds
                : projectIdsOptions
            }
            hasRequiredAsterisk
          />

          <FieldSelect
            name="odnType"
            label="ODN Type"
            options={odnTypesOptions ?? []}
            hasRequiredAsterisk
          />
        </FieldsetThreeColumn>

        <FieldsetThreeColumn>
          <Legend>Autofill</Legend>

          <VerticalGaps>
            <InputWithLabel
              value={apd?.suffixPermit}
              label="Permit ID"
              isDisabled
            />
            <InputWithLabel value={apd?.fdtId} label="FDTID" isDisabled />
            <InputWithLabel
              value={apd?.oltIdaction}
              label="OLT-ID"
              isDisabled
            />
            <InputWithLabel value={apd?.hpDesign} label="HP APD" isDisabled />
          </VerticalGaps>

          <VerticalGaps>
            <InputWithLabel
              value={apd?.cityTown}
              label="City Town"
              isDisabled
            />
            <InputWithLabel
              value={apd?.projectName}
              label="Project Name"
              isDisabled
            />
            <InputWithLabel
              value={currentProjectDescription}
              label="Project Description"
              isDisabled
            />
          </VerticalGaps>

          <VerticalGaps>
            <InputWithLabel
              value={apd?.drawingIdApd}
              label="Drawing ID APD"
              isDisabled
            />
            <InputWithLabel value={apd?.oltType} label="OLT Type" isDisabled />
            <InputWithLabel
              value={apd?.combineStatus}
              label="Combine Status"
              isDisabled
            />
          </VerticalGaps>
        </FieldsetThreeColumn>

        <FieldsetThreeColumn>
          <Legend>Input by Vendor</Legend>

          <div className="grid grid-cols-3 gap-4">
            <VerticalGaps>
              <FieldInput
                name="totalResidenceHomepass"
                label="Total Residence Homepass"
                type="number"
                hasRequiredAsterisk
              />
              <FieldInputDate label="Date Feeder Done" name="dateFeederDone" />
              <FieldInputDate label="Date OLT Done" name="dateOltDone" />
              <FieldTextarea label="Remarks" name="remarks" />
            </VerticalGaps>

            <VerticalGaps>
              <FieldInput
                name="totalBizzHomepass"
                label="Total Bizz Homepass"
                type="number"
                hasRequiredAsterisk
              />
              <FieldHomepassInvalid
                hpApd={hpApdInt}
                hpRfs={totalHpRfsInt}
                apdId={apd?.id}
                isDisabled={needInvalid}
              />
              <FieldSelect
                name="fdtType"
                label="FDT Type"
                options={fdtTypeOptions ?? []}
                hasRequiredAsterisk
              />
            </VerticalGaps>

            <VerticalGaps>
              <InputWithLabel
                value={totalHpRfs}
                label="Total HP RFS (ABD)"
                isDisabled
                hasRequiredAsterisk
              />
              <FieldHomepassAdditional
                hpApd={hpApdInt}
                hpRfs={totalHpRfsInt}
                apdId={apd?.id}
                isDisabled={needAdditional}
              />
              <FieldSelect
                name="category"
                label="Category Data"
                options={dataCategoryOptions ?? []}
                hasRequiredAsterisk
              />
            </VerticalGaps>

            <div className="col-span-full">
              <FieldFile
                label="Upload document ABD.kmz"
                name="media"
                accept=".kmz"
                hasRequiredAsterisk
              />
            </div>
          </div>
        </FieldsetThreeColumn>
      </FormContainer>
      <FormFooter>
        <Anchor href="/rfs/rfs" mood="axiata-blue-outline">
          Cancel
        </Anchor>
        <SubmitButton
          form={formId}
          loading={isPending || isUploadingMedia}
          disabled={!isValid}
        />
      </FormFooter>

      {downloadResultButton && (
        <>
          <div className="text-right">
            <DownloadButton onClick={handleDownload} loading={isDownloading}>
              Download Validation Result
            </DownloadButton>
          </div>

          {rfsId && <RFSInvalidTable rfsId={rfsId} />}
        </>
      )}
    </>
  );
}
function optionsFromProperty<T>(data: T[], property: keyof T) {
  const result = [];
  const existing = new Set();
  for (const datum of data) {
    if (!existing.has(datum[property])) {
      result.push({
        value: datum[property],
        label: datum[property],
      });
      existing.add(datum[property]);
    }
  }
  return result;
}

function ModalRFSInvalid({ rfsId }: { rfsId: string }) {
  const { data: rfsInvalids } = useRFSInvalids({
    rfsId: rfsId,
  });

  const keyMapping: { [key: string]: string } = {
    clusterId: "ClusterID",
    pole: "Pole",
    fdtId: "FDTID",
    fatId: "FATID",
    cableDistribution: "CABLE DISTRIBUTION",
    cableDrop: "CABLE DROP",
    folderStructureAndSequence: "Struktur/Urutan Folder",
    subFolder: "Sub Folder",
    folderName: "Foldername",
    symbol: "Symbol",
    requiredFolder: "REQUIRED FOLDER",
    homepass: "Homepass",
  };

  const formatKey = (key: string | null) => {
    if (!key) return "-";
    return keyMapping[key] || key;
  };

  return (
    <div className="overflow-x-auto">
      <table className="w-full table-auto border-collapse">
        <thead>
          <tr>
            <th className="p-4 text-left text-sm font-medium">No</th>
            <th className="p-4 text-left text-sm font-medium">Description</th>
            <th className="p-4 text-left text-sm font-medium">
              Validation Result
            </th>
          </tr>
        </thead>
        <tbody>
          {rfsInvalids?.map((rfsInvalid, index: number) => (
            <tr key={rfsInvalid.id} className="border-y">
              <td className="p-4 text-left text-sm">{index + 1}</td>
              <td className="p-4 text-left text-sm">
                {rfsInvalid.key ? formatKey(rfsInvalid.key) : "-"}
              </td>
              <td className="flex justify-center p-4 text-sm">
                {rfsInvalid.valid ? (
                  <CheckSquare2 color="#5dee7a" />
                ) : (
                  <SquareX color="#f44336" />
                )}
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}
